import React, { useRef } from 'react';
import styled from 'styled-components';
import { below, size } from '../../style/util';
import { neutral } from '../../style/colors';
import SidebarButton from '../common/Buttons/SidebarButton';
import KommuneSelect from './KommuneSelect';
import Logo from './Logo';
import { useSpring, animated } from 'react-spring';
import { IKommune } from '../../Interfaces/IKommune';
import { useMedia } from '../../hooks/useMedia';

const Splash = React.memo((props: SplashPropTypes) => {
  const { show, onGoClick, kommuner } = props;
  const kommuneRef = useRef(null);
  const fade = useSpring({
    opacity: show ? 1 : 0,
  });

  const isSmallScreen = useMedia(
    [`(max-width: ${size.small}px)`],
    [true],
    false
  );
  const renderLargeScreenLogo = () => {
    if (!isSmallScreen) {
      return (
        <LogoWrapper>
          <Logo />
        </LogoWrapper>
      );
    }
    return null;
  };
  const renderSmallScreenLogo = () => {
    if (isSmallScreen) {
      return <Logo width={50} />;
    }
    return null;
  };

  const wrapperStyle = {
    position: 'absolute' as any,
    zIndex: 3,
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: 'hsla(209, 61%, 16%, 0.4)',
    pointerEvents: show ? ('auto' as any) : ('none' as any),
  };
  return (
    <animated.div style={{ ...wrapperStyle, ...fade }}>
      <SplashWrapper>
        <Main>
          {renderLargeScreenLogo()}
          <MainContent>
            <h1>
              Velkommen til{' '}
              <span style={{ fontWeight: 900 }}>
                PlanDK<span style={{ color: '#98C3D1' }}>2</span>
              </span>
            </h1>
            {renderSmallScreenLogo()}
            <p>
              Plandata er et digitalt register for fysisk planlægning i Danmark.
              Det sikrer, at plandata er entydige og digitalt tilgængelige. Alle
              kan søge lokalplaner og andre plantyper i kortmodulet.
            </p>
            <KommuneWrapper>
              <h2>Start fra en kommune</h2>
              <KommuneInputWrapper>
                <span style={{ flex: 1 }} ref={kommuneRef}>
                  <KommuneSelect kommuner={kommuner} />
                </span>
                <SidebarButton
                  text={'GÅ'}
                  onClick={() => {
                    onGoClick(
                      kommuner.find(
                        k => k.kode === kommuneRef.current.children[0].value
                      )
                    );
                  }}
                  width={isSmallScreen ? '100%' : '142px'}
                  marginLeft={isSmallScreen ? '0' : '12px'}
                  marginTop={isSmallScreen ? '12px' : '0'}
                />
              </KommuneInputWrapper>
            </KommuneWrapper>
          </MainContent>
        </Main>
        <Footer>
          <p>
            Lavet af <a href="https://www.septima.dk">Septima P/S</a> for{' '}
            <a href="https://erhvervsstyrelsen.dk/">Erhvervsstyrelsen</a>. Dette
            er en demoklient for visning af plandata. Data er ikke kontinuerlig
            opdateret og kan derfor være ufuldstændig.
          </p>
        </Footer>
      </SplashWrapper>
    </animated.div>
  );
});

const SplashWrapper = styled.div`
  position: absolute;
  z-index: 4;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  margin-left: auto;
  margin-right: auto;
  margin-top: auto;
  margin-bottom: auto;
  width: 990px;
  height: 600px;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  box-shadow: 0 20px 40px 0 rgba(8, 29, 45, 0.2);

  ${(below as any).small`
    left: 20px;
    right: 20px;
    top: 20px;
    bottom: 20px;
    width: unset;
  `}
`;

const Main = styled.div`
  flex: 1;
  display: flex;
  font-size: 13px;
`;
const Footer = styled.div`
  background-color: ${neutral[100]};
  height: 75px;
  border-radius: 0 0 5px 5px;
  border-top: ${neutral[200]} solid 2px;
  padding: 0px 210px;
  color: ${neutral[400]};
  display: flex;
  align-items: center;
  text-align: center;
  font-size: 12px;
  ${(below as any).small`
    padding: 0 20px;
  `}
`;

const LogoWrapper = styled.div`
  width: 350px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const MainContent = styled.div`
  flex: 1;
  padding-top: 110px;
  padding-right: 200px;
  display: flex;
  flex-direction: column;
  h1 {
    font-size: 36px;
    font-weight: 400;
    letter-spacing: 0.03em;
  }
  p {
    margin: 38px 0;
    line-height: 1.8em;
    color: #8fa1b4;
  }
  ${(below as any).small`
    padding: 20px;
    align-items: center;
  `}
`;

const KommuneWrapper = styled.div`
  ${(below as any).small`
    align-self: stretch;
  `}
`;
const KommuneInputWrapper = styled.div`
  display: flex;
  ${(below as any).small`
    flex-direction: column;
  `}
`;

interface SplashPropTypes {
  show: boolean;
  onGoClick: Function;
  kommuner: Array<IKommune>;
}

export default Splash;
